import { getClientId } from '@ixigo/pwa';
import { getCookieHelpers } from '@/utils/cookie';
import { flattenToLokiFields } from './flatten-loki-fields';

const LOKI_LOGS_URL =
	import.meta.env.PUBLIC_LOKI_LOGS_URL || 'https://collector.ixigo.com/logs/ui';

const SESSION_ID_COOKIE = 'sessionId';

type LokiLevel = 'error' | 'info';

export type LokiFields = Record<
	string,
	string | number | boolean | null | undefined
>;

/**
 * Fire-and-forget POST to the Loki / Kafka REST proxy (`/logs/ui`). Non-blocking
 * (deferred a tick) and never throws — logging must never affect UX. Shared by
 * the per-domain loggers (manage-payments errors, CBCC CVP info, …); each owns
 * its own `key` namespace and decides the level.
 */
export function postLokiRecord(
	level: LokiLevel,
	key: string,
	fields?: LokiFields,
	extraValues?: LokiFields,
): void {
	if (typeof window === 'undefined' || !LOKI_LOGS_URL) return;

	try {
		const cookies = getCookieHelpers();

		const value: Record<string, string> = {
			type: level,
			clientId: getClientId(),
			deviceId: cookies.getCookie('ixiUID')?.trim() ?? '',
			appVersion: cookies.getCookie('appVersion')?.trim() ?? '',
			sessionId: cookies.getCookie(SESSION_ID_COOKIE)?.trim() ?? '',
			key,
			url: window.location.href,
			...flattenToLokiFields(fields ?? {}),
			...flattenToLokiFields(extraValues ?? {}),
		};

		const deviceId = value.deviceId ?? '';

		setTimeout(() => {
			// Starting fetch can itself throw synchronously in a mocked, bridged, or
			// partially initialised runtime. Entering through a promise contains that
			// path as well as ordinary request rejections.
			Promise.resolve()
				.then(() =>
					fetch(LOKI_LOGS_URL, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/vnd.kafka.json.v2+json',
							deviceId,
						},
						body: JSON.stringify({
							records: [{ value: { appName: 'ixi-finance', ...value } }],
						}),
					}),
				)
				.catch(() => {});
		}, 0);
	} catch {
		// Context/cookie/serialization failures are logging failures too.
	}
}
